import React, { useRef, useState } from "react";
import {
  DateField,
  EditButton,
  FunctionField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useGetOne,
  useNotify,
  useRefresh
} from "react-admin";
import { useTokenOps } from "../../components/TokenContext";
import { hostname } from "../../providers/api";
import CalculationTab from "./components/CalculationTab";
import ProjectVersionsSection from "./components/ProjectVersionsSection";
import { useLcaRun } from "./useLcaRun";
import CalculatedDataUploadSection from "./components/CalculatedDataUploadSection";
import ContextSection from "./components/ContextSection";
import DuplicateProjectButton from "./components/DuplicateProjectButton";
import { useActiveBcomeVersionMap, useBcomeVersionRecordMap } from "../../components/DbVersion";
import { parseTraceabilityIssues } from "../traceabilityIssues";
import { pollUploadHistory } from "./traceabilityVersionUpdate";
import { usePendingTraceabilityVersion } from "./usePendingTraceabilityVersion";

// The project name heads the page, on the same line as its actions.
const ProjectShowActions = ({ basePath, data, accessToken }) => (
  <TopToolbar style={{ justifyContent: "space-between", alignItems: "center" }}>
    <h2 style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>{data?.name}</h2>
    <div style={{ display: "flex", alignItems: "center" }}>
      <EditButton basePath={basePath} record={data} />
      <DuplicateProjectButton record={data} accessToken={accessToken} />
    </div>
  </TopToolbar>
);

const ShowProjects = (props) => {
  const ref = useRef();
  const calculatedFileRef = useRef();
  const [wholeExcelErrors, setWholeExcelErrors] = useState(undefined);
  const [traceabilityWarnings, setTraceabilityWarnings] = useState([]);
  // Bumped when a traceability upload starts or finishes, so the last-upload section reloads
  const [lastUploadReloadKey, setLastUploadReloadKey] = useState(0);
  const reloadLastUpload = () => setLastUploadReloadKey(key => key + 1);
  const notify = useNotify();
  const refresh = useRefresh();
  const tokenOps = useTokenOps();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadOptions, setUploadOptions] = useState({
    traceability: true,
    ecomReferences: false,
    qrCodes: false
  });
  const [backups, setBackups] = useState([]);
  const [isCalculateable, setIsCalculateable] = useState(false);
  const [isCircularityCalculateable, setIsCircularityCalculateable] = useState(false);

  // Only Active=true versions in the upload selector; labels of existing
  // records resolve via the unfiltered useBcomeVersionRecordMap below.
  const bcomeVersionMap = useActiveBcomeVersionMap();
  const bcomeVersionRecordMap = useBcomeVersionRecordMap();
  const [selectedBcomeVersionId, setSelectedBcomeVersionId] = useState("");
  const { data: project } = useGetOne(props.resource || "projects", props.id, {
    enabled: !!props.id,
  });
  const projectBcomeVersionId = project?.bcomeVersionId;

  // Default the Version Set selector to the project's own Version Set, when it has one
  // and it is selectable (only active sets are listed and accepted for upload).
  // Never overrides a choice already made by the user.
  React.useEffect(() => {
    if (
      !selectedBcomeVersionId &&
      projectBcomeVersionId &&
      bcomeVersionMap[projectBcomeVersionId]
    ) {
      setSelectedBcomeVersionId(projectBcomeVersionId);
    }
  }, [projectBcomeVersionId, bcomeVersionMap, selectedBcomeVersionId]);

  // Upload tab states
  const [selectedCalculatedFile, setSelectedCalculatedFile] = useState(null);
  const [calculatedUploadOptions, setCalculatedUploadOptions] = useState({
    supplyMap: false,
    lca: false,
    waterConsumption: false,
    circularity: false,
    ecoScore: false,
    careAndUse: false
  });

  // Individual loading states for each action
  const [loadingStates, setLoadingStates] = useState({
    validateTraceability: false,
    validateEcom: false,
    refineTraceability: false,
    updateVersion: false,
    downloadTraceability: false,
    upload: false,
    uploadArticleImages: false,
    deleteEcom: false,
    downloadBackupSupplyMap: false,
    downloadBackupRecovery: false,
    downloadCircularity: false,
    runCalculation: false,
    runCircularity: false,
    recoverBackup: false,
    loadingBackups: false,
    uploadCalculatedData: false
  });

  // Whether LCA / Circularity can run depends on the project's Version Set, so it is
  // fetched again after a traceability upload.
  const refreshCalculateable = React.useCallback(() => {
    fetch(`${hostname}/lca-supply/calculateable/${props.id}`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then(response => response.json())
      .then(data => {
        setIsCalculateable(data.isCalculateable === true);
      })
      .catch(error => {
        // Silently handle error - not calculateable
        console.log('Calculateable error:', error)
        setIsCalculateable(false);
      });

    // Fetch circularity calculateable status (IT-3197)
    fetch(`${hostname}/circularity-calculation/calculateable/${props.id}`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then(response => response.json())
      .then(data => {
        setIsCircularityCalculateable(data.isCalculateable === true);
      })
      .catch(error => {
        console.log('Circularity calculateable error:', error)
        setIsCircularityCalculateable(false);
      });
  }, [props.id, tokenOps.account?.accessToken]);

  // A finished traceability upload changes the project's Version Set on success (kept on
  // failure): reload the record (Traceability version, LCA version status) and flags.
  const onTraceabilityUploadSettled = () => {
    refresh();
    refreshCalculateable();
  };

  // Version Set of the traceability upload this page started: a Raw data upload (until
  // the request answers) or an Update Version auto-upload (until its history row is
  // final). Uploads started before the page was opened, or elsewhere, come from the
  // upload history instead (needs a backend that records the row's bcomeVersionId).
  const [uploadingBcomeVersionId, setUploadingBcomeVersionId] = useState(null);
  const backgroundPendingBcomeVersionId = usePendingTraceabilityVersion({
    projectId: props.id,
    accessToken: tokenOps.account?.accessToken,
    reloadKey: lastUploadReloadKey,
    onSettled: onTraceabilityUploadSettled,
  });
  const pendingBcomeVersionId = uploadingBcomeVersionId || backgroundPendingBcomeVersionId;

  // Fetch backup info and calculateable status on component mount
  React.useEffect(() => {
    if (props.id) {
      // Fetch backup list
      setLoadingStates(prev => ({ ...prev, loadingBackups: true }));
      fetch(`${hostname}/project-backup/${props.id}`, {
        headers: {
          authorization: "Bearer " + tokenOps.account?.accessToken,
        },
      })
        .then(response => {
          if (!response.ok) {
            // No backups available (404 or other error status)
            setBackups([]);
            return null;
          }
          return response.json();
        })
        .then(data => {
          if (data && Array.isArray(data) && data.length > 0) {
            // Valid backup list with items
            setBackups(data);
          } else {
            // Empty array, error response, or no data
            setBackups([]);
          }
        })
        .catch(error => {
          // Network error or other exception
          console.log('Backup list error:', error)
          setBackups([]);
        })
        .finally(() => setLoadingStates(prev => ({ ...prev, loadingBackups: false })));

      refreshCalculateable();
    }
  }, [props.id]);

  const validateExcel = (validatePath) => () => {
    const warningMessage = validatePath === "simulateUploadTraceability"
      ? "Traceability"
      : validatePath === "simulateUploadEcomExcel"
        ? "E-commerce references"
        : "Water consumption";

    const loadingKey = validatePath === "simulateUploadTraceability"
      ? "validateTraceability"
      : "validateEcom";

    // The traceability check resolves the Version Set by id only (never by the Excel version text)
    const isTraceabilityCheck = validatePath === "simulateUploadTraceability";
    if (isTraceabilityCheck && !selectedBcomeVersionId) {
      notify("Please select a Version Set", "warning");
      return;
    }
    if (isTraceabilityCheck) setTraceabilityWarnings([]);

    const data = new FormData();
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
    data.append("file", ref.current.files[0]);
    if (isTraceabilityCheck) {
      data.append("bcomeVersionId", selectedBcomeVersionId);
    }
    fetch(`${hostname}/projects/${props.id}/${validatePath}`, {
      method: "POST",
      body: data,
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status < 400 && isTraceabilityCheck) {
          // Traceability warnings are listed below the buttons, with sheet/row/column
          const warnings = parseTraceabilityIssues(data.warnings);
          setWholeExcelErrors(undefined);
          setTraceabilityWarnings(warnings);
          if (warnings.length > 0) {
            notify(`Excel validated with ${warnings.length} warning(s). Please check them below.`, "warning");
          } else {
            notify("Excel validated successfully", "success");
          }
        } else if (data.status < 400) {
          notify("Excel validated successfully", "success");
          setWholeExcelErrors(undefined);

          if (data.warnings) {
            notify(`Warning! You have Duplicate key error(s) in Articles, make sure to delete ${warningMessage} before uploading the file.`, "warning");
          }
        } else {
          notify(`Validation failed. Please check errors below.`, "error");
          setWholeExcelErrors(data);
        }
      })
      .catch(error => {
        notify(`Error: ${error.message}`, "error");
      })
      .finally(() => setLoadingStates(prev => ({ ...prev, [loadingKey]: false })));
  }

  const upload = (action) => () => {
    const loadingKey = action === "uploadArticleImages" ? "uploadArticleImages" : "upload";
    const data = new FormData();
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
    data.append("file", ref.current.files[0]);
    fetch(`${hostname}/projects/${props.id}/${action}`, {
      method: "POST",
      body: data,
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        if (response.status < 400) {
          const message =
            action === "uploadTraceability"
              ? { message: "Excel uploaded and processing", type: 'info' }
              : { message: "Excel uploaded successfully", type: 'success' };

          notify(message.message, message.type);
        } else {
          notify(`Error: ${response.message}`, "warning");
          if (action === "uploadTraceability") {
            setWholeExcelErrors(response);
          }
        }
      })
      .catch(error => {
        notify(`Error: ${error.message}`, "warning");
      })
      .finally(() => {
        setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
      });
  };

  const uploadRawData = async () => {
    if (!ref.current?.files?.[0]) {
      notify("Please select a file first", "warning");
      return;
    }

    if (!selectedBcomeVersionId) {
      notify("Please select a Version Set", "warning");
      return;
    }

    const file = ref.current.files[0];
    const data = new FormData();
    data.append("file", file);
    data.append("bcomeVersionId", selectedBcomeVersionId);

    // Determinar qué endpoints llamar según los checkboxes
    const endpoints = [];
    if (uploadOptions.traceability) endpoints.push("uploadTraceability");
    if (uploadOptions.ecomReferences) endpoints.push("uploadEcomExcel");
    if (uploadOptions.qrCodes) endpoints.push("uploadArticleImages");

    if (endpoints.length === 0) {
      notify("Please select at least one upload type", "warning");
      return;
    }

    // Estado de Cargando (AZUL) - Mostrar inmediatamente
    notify("File is being uploaded to the system. You can check the status in the Upload status section.", "info");
    setLoadingStates(prev => ({ ...prev, upload: true }));
    if (uploadOptions.traceability) setUploadingBcomeVersionId(selectedBcomeVersionId);

    const successes = [];
    const errors = [];

    // Llamar a todos los endpoints de forma secuencial
    for (const endpoint of endpoints) {
      const reqStart = Date.now();
      try {
        const response = await fetch(`${hostname}/projects/${props.id}/${endpoint}`, {
          method: "POST",
          body: data,
          headers: {
            authorization: "Bearer " + tokenOps.account?.accessToken,
          },
        });

        if (response.status < 400) {
          // Respuesta exitosa (2xx, 3xx)
          successes.push(endpoint);
        } else {
          // Respuesta de error (4xx, 5xx)
          const errorText = await response.text();
          errors.push({ endpoint, error: errorText });
        }
      } catch (error) {
        // Error de red o sin respuesta del backend — mensaje informativo en vez de "Failed to fetch"
        errors.push({ endpoint, error: describeRequestError(error, Date.now() - reqStart) });
      }
      // Traceability is done (committed or rolled back): show the project's own version again.
      if (endpoint === "uploadTraceability") {
        setUploadingBcomeVersionId(null);
        onTraceabilityUploadSettled();
      }
    }

    // Estado de Éxito (VERDE) - Cuando todas las cargas son exitosas
    if (successes.length > 0 && errors.length === 0) {
      notify("File uploaded successfully.", "success");
    } else if (successes.length > 0 && errors.length > 0) {
      // Éxito parcial
      notify(`Partially uploaded: ${successes.join(", ")}`, "success");
    }

    // Estado de Error (ROJO) - Cuando hay errores
    if (errors.length > 0) {
      errors.forEach(({ error }) => {
        notify(`Upload failed: ${error}`, "error", { autoHideDuration: 12000 });
      });
    }

    if (uploadOptions.traceability) reloadLastUpload();
    setLoadingStates(prev => ({ ...prev, upload: false }));
  };

  const remove = (action) => () => {
    const METHOD = action === "deleteTraceability" ? "POST" : "DELETE";
    setLoadingStates(prev => ({ ...prev, deleteEcom: true }));
    fetch(`${hostname}/projects/${props.id}/${action}`, {
      method: METHOD,
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    }).then((response) => {
      if (response.status < 400) {
        notify("Excel deleted successfully", "success");
      } else {
        notify(`Error: ${response.statusText}`, "warning");
      }
    })
      .finally(() => setLoadingStates(prev => ({ ...prev, deleteEcom: false })));
  }

  // Builds a download filename for the refined / version-updated traceability file.
  // The backend may also send a Content-Disposition filename, which takes precedence.
  const buildTraceabilityFilename = (label) => {
    const now = new Date();
    const date = now.toISOString().split("T")[0]; // YYYY-MM-DD
    const time = now.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS
    const accountName = tokenOps.account?.data?.accountName || "Unknown";
    // '|' is not allowed in Windows filenames, so the parts are joined with '-'
    return `${accountName} - Traceability - ${label} - ${date} - ${time}.xlsx`;
  };

  // Reads an Excel blob response and triggers a browser download.
  const downloadExcelResponse = async (response, fallbackName) => {
    const arrayBuffer = await response.arrayBuffer();
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    let filename = fallbackName;
    const disposition = response.headers.get("content-disposition");
    if (disposition) {
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^";]+)["']?/i);
      if (match && match[1]) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch {
          filename = match[1];
        }
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Builds a user-facing message from a thrown fetch error, tailored to WHAT failed so a
  // slow-network failure isn't misreported as "the service is down" (it isn't — other users
  // hit the same endpoint fine):
  //   - AbortError → our client-side safety timeout fired (no response in time).
  //   - TypeError / "failed to fetch" after a noticeable delay → the connection was cut
  //     mid-request. These endpoints are cross-origin and legitimately take ~20s, so this is
  //     typically a slow/unstable network, a VPN, or a proxy/gateway timeout whose error
  //     response carries no CORS headers — the browser then only sees "failed to fetch".
  //   - TypeError almost immediately → the service is genuinely unreachable.
  //   - anything else → surface the message (e.g. thrown from a 4xx/5xx response body).
  const describeRequestError = (error, elapsedMs = 0) => {
    const seconds = Math.max(1, Math.round(elapsedMs / 1000));
    const isNetworkError =
      error instanceof TypeError || /failed to fetch/i.test(error?.message || "");

    if (error?.name === "AbortError") {
      return `The request timed out after ${seconds}s with no response from the server. ` +
        `It may still be processing — wait a moment and check before trying again.`;
    }
    if (isNetworkError && elapsedMs >= 8000) {
      return `The connection dropped after ${seconds}s before the server responded. This is ` +
        `usually a slow or unstable network, a VPN, or a proxy/gateway timeout — not a problem ` +
        `with the file. If retrying keeps failing, try again on a different network or without VPN.`;
    }
    if (isNetworkError) {
      return "Could not reach the server. Please check the service is available and try again.";
    }
    return `Error: ${error.message}`;
  };

  // Emits the toast for a failed request and logs diagnostics (name/message/elapsed) so a
  // case that "always fails for one person" can be confirmed from the console. Uses a longer
  // autoHideDuration because the network/timeout messages are longer than a default toast.
  const notifyRequestError = (error, elapsedMs = 0) => {
    console.error("[traceability] request failed", {
      name: error?.name,
      message: error?.message,
      elapsedMs,
    });
    notify(describeRequestError(error, elapsedMs), "error", { autoHideDuration: 12000 });
  };

  // Refinement button — "Validar & Uniformizar Traza".
  // Sends the uploaded traceability Excel; backend validates/uniformizes it and
  // returns the refined workbook as a file download (autoUploadTraceability = false).
  const refineTraceability = () => {
    if (!ref.current?.files?.[0]) {
      notify("Please select a file first", "warning");
      return;
    }

    const data = new FormData();
    data.append("file", ref.current.files[0]);
    data.append("autoUploadTraceability", "false");

    setLoadingStates(prev => ({ ...prev, refineTraceability: true }));
    notify("File is being updated and analyzed....", "info");

    // Safety cap so a hung connection eventually surfaces an error instead of an endless
    // spinner. Generous on purpose: the request legitimately takes ~20s, and any proxy
    // timeout fires well before this and is reported via the catch below with its elapsed time.
    const startTime = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min

    fetch(`${hostname}/projects/${props.id}/refineTraceability`, {
      method: "POST",
      body: data,
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status >= 400) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || errData.error || `Traceability refinement failed (HTTP ${response.status})`);
        }
        await downloadExcelResponse(response, buildTraceabilityFilename("Refinement"));
        setWholeExcelErrors(undefined);
        setTraceabilityWarnings([]);
        notify("Refined traceability file downloaded successfully", "success");
      })
      .catch((error) => {
        notifyRequestError(error, Date.now() - startTime);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoadingStates(prev => ({ ...prev, refineTraceability: false }));
      });
  };

  // Message for a failed traceability request: the backend sends a summary in `message`
  // and, for generation errors, the cause in `error`.
  const traceabilityErrorMessage = (errData, fallback) =>
    [errData?.message, errData?.error].filter(Boolean).join(": ") || fallback;

  // Polling of background version updates stops when the page is left.
  const unmountedRef = useRef(false);
  React.useEffect(() => () => {
    unmountedRef.current = true;
  }, []);

  const fetchUploadHistoryRow = async (uploadHistoryId) => {
    const response = await fetch(`${hostname}/uploadHistory/${uploadHistoryId}`, {
      headers: { authorization: "Bearer " + tokenOps.account?.accessToken },
    });
    if (response.status >= 400) {
      throw new Error(`Upload status request failed (HTTP ${response.status})`);
    }
    return response.json();
  };

  // Update Version button — "Actualizar Versión" (IT-3586).
  // The updated workbook is generated from the project data already stored in DB
  // (updateProjectVersion). Two modes:
  //   - download (default): the workbook is returned as a file download.
  //   - autoUpload: the backend uploads it as traceability linked to bcomeVersionId,
  //     in background; Admin polls the upload-history row until it finishes.
  // An empty targetVersion lets the backend default to its latest version.
  const updateVersion = (targetVersion, { autoUpload = false, bcomeVersionId } = {}) => {
    if (autoUpload) {
      updateVersionAndUpload(targetVersion, bcomeVersionId);
      return;
    }

    setLoadingStates(prev => ({ ...prev, updateVersion: true }));
    notify("File is being updated and analyzed....", "info");

    // Safety cap + elapsed tracking — see refineTraceability for the rationale.
    const startTime = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min

    fetch(`${hostname}/projects/${props.id}/updateProjectVersion`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
      body: JSON.stringify({
        ...(targetVersion ? { targetVersion } : {}),
        autoUploadTraceability: false,
      }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status >= 400) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(traceabilityErrorMessage(errData, `Traceability version update failed (HTTP ${response.status})`));
        }
        await downloadExcelResponse(response, buildTraceabilityFilename("Version update"));
        notify("Updated traceability file downloaded successfully", "success");
      })
      .catch((error) => {
        notifyRequestError(error, Date.now() - startTime);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoadingStates(prev => ({ ...prev, updateVersion: false }));
      });
  };

  const updateVersionAndUpload = async (targetVersion, bcomeVersionId) => {
    setLoadingStates(prev => ({ ...prev, updateVersion: true }));
    const startTime = Date.now();

    try {
      const response = await fetch(`${hostname}/projects/${props.id}/updateProjectVersion`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + tokenOps.account?.accessToken,
        },
        body: JSON.stringify({
          ...(targetVersion ? { targetVersion } : {}),
          autoUploadTraceability: true,
          bcomeVersionId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status >= 400) {
        throw new Error(traceabilityErrorMessage(data, `Traceability version update failed (HTTP ${response.status})`));
      }

      notify(
        `Updating to ${targetVersion} and uploading traceability. You can follow it in Traceability status.`,
        "info",
        { autoHideDuration: 8000 }
      );
      // The update runs in background: show the target Version Set until it finishes.
      setUploadingBcomeVersionId(bcomeVersionId);
      reloadLastUpload();

      const finalRow = await pollUploadHistory({
        fetchRow: () => fetchUploadHistoryRow(data.uploadHistoryId),
        isCancelled: () => unmountedRef.current,
      });
      if (unmountedRef.current) return;
      // Polling gave up while the update may still run: keep showing it as pending.
      if (finalRow) setUploadingBcomeVersionId(null);

      if (!finalRow) {
        notify(
          "The update is still running. Check Traceability status for the result.",
          "warning",
          { autoHideDuration: 12000 }
        );
      } else if (finalRow.status === "FAILED") {
        notify(
          `Version update failed, the project keeps its previous version and data: ${finalRow.stacktrace || "see Traceability status"}`,
          "error",
          { autoHideDuration: 20000 }
        );
        onTraceabilityUploadSettled();
      } else {
        notify(
          `Project updated to ${targetVersion} and traceability uploaded. Run calculation when ready.`,
          "success",
          { autoHideDuration: 12000 }
        );
        onTraceabilityUploadSettled();
      }
    } catch (error) {
      notifyRequestError(error, Date.now() - startTime);
    } finally {
      if (!unmountedRef.current) {
        reloadLastUpload();
        setLoadingStates(prev => ({ ...prev, updateVersion: false }));
      }
    }
  };

  // Download button (IT-3586): the project's traceability Excel rebuilt from DB in its
  // current version, without updating it (downloadTraceability, admin only).
  const downloadTraceability = () => {
    setLoadingStates(prev => ({ ...prev, downloadTraceability: true }));
    notify("Generating traceability file...", "info");

    const startTime = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min

    fetch(`${hostname}/projects/${props.id}/downloadTraceability`, {
      method: "POST",
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status >= 400) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(traceabilityErrorMessage(errData, `Traceability download failed (HTTP ${response.status})`));
        }
        await downloadExcelResponse(response, buildTraceabilityFilename("Download"));
        notify("Traceability file downloaded successfully", "success");
      })
      .catch((error) => {
        notifyRequestError(error, Date.now() - startTime);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoadingStates(prev => ({ ...prev, downloadTraceability: false }));
      });
  };

  const downloadBackupRecovery = async (record) => {
    const latestBackup = backups[0];
    console.log('Download Recovery - Latest backup:', latestBackup);
    console.log('Download Recovery - Record:', record);

    if (!latestBackup) {
      notify("No backups available", "warning");
      return;
    }

    const backupId = latestBackup.id || latestBackup._id || latestBackup.backupId;
    console.log('Download Recovery - Backup ID:', backupId);

    if (!backupId) {
      console.error('Backup object keys:', Object.keys(latestBackup));
      notify("Invalid backup data", "error");
      return;
    }

    setLoadingStates(prev => ({ ...prev, downloadBackupRecovery: true }));

    try {
      console.log('Fetching backup...');
      const response = await fetch(`${hostname}/project-backup/download/${backupId}`, {
        method: "GET",
        headers: {
          authorization: "Bearer " + tokenOps.account?.accessToken,
        },
      });

      console.log('Backup response status:', response.status);
      console.log('Backup Content-Type:', response.headers.get('content-type'));
      console.log('Backup Content-Length:', response.headers.get('content-length'));

      if (response.status >= 400) {
        throw new Error(response.statusText);
      }

      console.log('Getting backup blob via arrayBuffer...');

      // Use arrayBuffer instead of blob to avoid hanging
      const arrayBuffer = await response.arrayBuffer();
      console.log('Backup ArrayBuffer received, size:', arrayBuffer.byteLength);

      // Create blob from arrayBuffer
      const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      console.log('Backup blob created successfully');
      console.log('Backup blob size:', blob.size);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Format date and time
      const now = new Date();
      const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS

      // Get account name and project name
      const accountName = tokenOps.account?.data?.accountName || 'Unknown';
      const projectName = record?.name || 'Unknown';

      // Build filename: [Account name] - [Project name] - Backup - [Date] - [Time].xlsx
      // Note: Using '-' instead of '|' because '|' is not allowed in Windows filenames
      const filename = `${accountName} - ${projectName} - Backup - ${date} - ${time}.xlsx`;
      console.log('Backup download filename:', filename);

      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Backup download completed successfully');
      notify("Backup downloaded successfully", "success");
    } catch (error) {
      console.error('Error downloading backup:', error);
      notify(`Error downloading backup: ${error.message}`, "error");
    } finally {
      console.log('Setting backup loading to false');
      setLoadingStates(prev => ({ ...prev, downloadBackupRecovery: false }));
    }
  };

  const recoverBackup = () => {
    if (!backups || backups.length === 0) {
      notify("No backups available", "warning");
      return;
    }
    setLoadingStates(prev => ({ ...prev, recoverBackup: true }));
    fetch(`${hostname}/lca-supply/rollback/${props.id}`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => response.json().then(data => ({ status: response.status, data })))
      .then(({ status, data }) => {
        if (status < 400) {
          notify("Backup recovery initiated successfully", "success");
          refresh();
        } else {
          const errorMessage = data.error || data.message || "Unknown error";
          notify(`Error: ${errorMessage}`, "error");
        }
      })
      .catch((error) => {
        notify(`Error recovering backup: ${error.message}`, "error");
      })
      .finally(() => setLoadingStates(prev => ({ ...prev, recoverBackup: false })));
  };

  const [lcaDownloadStatus, setLcaDownloadStatus] = useState(null);
  const [circularityDownloadStatus, setCircularityDownloadStatus] = useState(null);
  const [calculationStatus, setCalculationStatus] = useState(null);
  const [circularityStatus, setCircularityStatus] = useState(null);
  const runCalculation = useLcaRun({
    accountId: tokenOps.account?.data?.accountId,
    projectId: props.id,
    hostname,
    notify,
    refresh,
    onBusy: busy => setLoadingStates(prev => ({ ...prev, runCalculation: busy })),
    onStatus: setCalculationStatus,
  });
  const runCircularity = useLcaRun({
    accountId: tokenOps.account?.data?.accountId,
    projectId: props.id,
    kind: 'circularity',
    hostname,
    notify,
    refresh,
    onBusy: busy => setLoadingStates(prev => ({ ...prev, runCircularity: busy })),
    onStatus: setCircularityStatus,
  });

  const downloadBackupSupplyMap = useLcaRun({
    accountId: tokenOps.account?.data?.accountId, projectId: props.id,
    hostname, notify, refresh, purpose: 'export',
    onBusy: busy => setLoadingStates(prev => ({ ...prev, downloadBackupSupplyMap: busy })),
    onStatus: setLcaDownloadStatus,
  });
  const downloadCircularity = useLcaRun({
    accountId: tokenOps.account?.data?.accountId, projectId: props.id,
    hostname, notify, refresh, kind: 'circularity', purpose: 'export',
    onBusy: busy => setLoadingStates(prev => ({ ...prev, downloadCircularity: busy })),
    onStatus: setCircularityDownloadStatus,
  });

  const uploadCalculatedData = async () => {
    if (!calculatedFileRef.current?.files?.[0]) {
      notify("Please select a file first", "warning");
      return;
    }

    const file = calculatedFileRef.current.files[0];
    const data = new FormData();
    data.append("file", file);

    // Mapeo de opciones a endpoints
    const endpointMap = {
      supplyMap: "uploadSupplyMap",
      lca: "uploadLCA",
      waterConsumption: "uploadWaterConsumption",
      circularity: "uploadCircularity",
      ecoScore: "uploadEcoScore",
      careAndUse: "uploadCareAndUse"
    };

    // Determinar qué endpoints llamar según los checkboxes
    const endpoints = [];
    Object.keys(calculatedUploadOptions).forEach(key => {
      if (calculatedUploadOptions[key]) {
        endpoints.push(endpointMap[key]);
      }
    });

    if (endpoints.length === 0) {
      notify("Please select at least one upload type", "warning");
      return;
    }

    setLoadingStates(prev => ({ ...prev, uploadCalculatedData: true }));

    const successes = [];
    const errors = [];

    // Llamar a todos los endpoints de forma secuencial
    for (const endpoint of endpoints) {
      const reqStart = Date.now();
      try {
        const response = await fetch(`${hostname}/projects/${props.id}/${endpoint}`, {
          method: "POST",
          body: data,
          headers: {
            authorization: "Bearer " + tokenOps.account?.accessToken,
          },
        });

        if (response.status < 400) {
          successes.push(endpoint);
        } else {
          const errorText = await response.text();
          errors.push({ endpoint, error: errorText });
        }
      } catch (error) {
        // Error de red o sin respuesta del backend — mensaje informativo en vez de "Failed to fetch"
        errors.push({ endpoint, error: describeRequestError(error, Date.now() - reqStart) });
      }
    }

    if (successes.length > 0) {
      notify(`Successfully uploaded: ${successes.join(", ")}`, "success");
    }

    if (errors.length > 0) {
      errors.forEach(({ endpoint, error }) => {
        notify(`Error uploading ${endpoint}: ${error}`, "error", { autoHideDuration: 12000 });
      });
    }

    setLoadingStates(prev => ({ ...prev, uploadCalculatedData: false }));
  };


  return (
    <>
      <Show
        {...props}
        actions={<ProjectShowActions accessToken={tokenOps.account?.accessToken} />}
      >
        <TabbedShowLayout>
          <Tab label="summary">
            <TextField source="dataCollectionURL" label="Data collection url" required={false} />
            <TextField source="folderURL" label="Folder url" required={false} />
            <TextField source="status" label="Status" />
            <DateField source="closingDate" required={false} />

            <TextField source="referenceType" label="References type" />
            <TextField source="functionalUnit" label="Functional unit" />
            <FunctionField
              label=""
              addLabel={false}
              render={(record) => (
                <ProjectVersionsSection
                  record={record}
                  versionSets={bcomeVersionRecordMap}
                  accessToken={tokenOps.account?.accessToken}
                  pendingBcomeVersionId={pendingBcomeVersionId}
                />
              )}
            />

            <DateField showTime source="createdAt" label="Creation date" />
            <DateField showTime source="updatedAt" label="Update date" />
          </Tab>

          <Tab label="calculation">
            <CalculationTab
              fileRef={ref}
              loadingStates={loadingStates}
              uploadOptions={uploadOptions}
              setUploadOptions={setUploadOptions}
              upload={upload}
              uploadRawData={uploadRawData}
              validateExcel={validateExcel}
              refineTraceability={refineTraceability}
              updateVersion={updateVersion}
              downloadTraceability={downloadTraceability}
              errors={wholeExcelErrors}
              traceabilityWarnings={traceabilityWarnings}
              lastUploadReloadKey={lastUploadReloadKey}
              remove={remove}
              downloadBackupSupplyMap={downloadBackupSupplyMap}
              downloadBackupRecovery={downloadBackupRecovery}
              recoverBackup={recoverBackup}
              runCalculation={runCalculation}
              runCircularity={runCircularity}
              calculationStatus={calculationStatus}
              circularityStatus={circularityStatus}
              lcaDownloadStatus={lcaDownloadStatus}
              circularityDownloadStatus={circularityDownloadStatus}
              downloadCircularity={downloadCircularity}
              isCircularityCalculateable={isCircularityCalculateable}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              hasBackup={backups && backups.length > 0}
              backups={backups}
              isCalculateable={isCalculateable}
              bcomeVersionMap={bcomeVersionMap}
              bcomeVersionRecordMap={bcomeVersionRecordMap}
              selectedBcomeVersionId={selectedBcomeVersionId}
              setSelectedBcomeVersionId={setSelectedBcomeVersionId}
              pendingBcomeVersionId={pendingBcomeVersionId}
            />
          </Tab>

          <Tab label="upload">
            <FunctionField label="" addLabel={false} render={record => (
              <div style={{ padding: "20px" }}>
                <ContextSection record={record} />
                <CalculatedDataUploadSection
                  fileRef={calculatedFileRef}
                  selectedFile={selectedCalculatedFile}
                  handleFileChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedCalculatedFile(file);
                    } else {
                      setSelectedCalculatedFile(null);
                    }
                  }}
                  uploadOptions={calculatedUploadOptions}
                  setUploadOptions={setCalculatedUploadOptions}
                  uploadCalculatedData={uploadCalculatedData}
                  loading={loadingStates.uploadCalculatedData}
                />
              </div>
            )} />
          </Tab>
        </TabbedShowLayout>
      </Show>
    </>
  );
};

export default ShowProjects;
